import { Button, Empty, Loading, Select } from "@components";
import styles from "@styles/containers/cdp/customer-fidelity.module.scss";
import classNames from "classnames/bind";
import { useEffect, useRef, useState } from "react";
import { If } from "shared/components/control-statements";
import { InputSearch } from "shared/components/InputSearch";
import { Currency } from "shared/utils/Currency";
import CashbackEvent from "./CashbackEvent";
import EditCashback from "./EditCashback";
import InternalBenefitAPI from "providers/InternalBenefitAPI";
import { useDebouncedCallback, useIsInViewport } from "@hooks";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
const css = classNames.bind(styles);

const filters = [
  {
    id: "ALL",
    name: "Tudo",
  },
  {
    id: "DEBIT",
    name: "Saídas",
  },
  {
    id: "CREDIT",
    name: "Entradas",
  },
  {
    id: "EXPIRATION",
    name: "Expirações",
  },
  {
    id: "REFUND",
    name: "Estornos",
  },
  {
    id: "MANUAL",
    name: "Manual",
  },
];

export const CustomerFidelity = ({ customer }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState(filters[0]);
  const [search, setSearch] = useState("");
  const [openEditCashback, setOpenEditCashback] = useState(false);

  const [loading, setLoading] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const [length, setLength] = useState(0);
  const [page, setPage] = useState(0);

  const [loadingPage, setLoadingPage] = useState(true);

  const [balance, setBalance] = useState(0);

  const [events, setEvents] = useState([]);

  const [brandSelected, setBrandSelected] = useState(null);
  const [brands, setBrands] = useState([]);

  const loadingRef = useRef(null);
  const isVisible = useIsInViewport(loadingRef);

  useEffect(() => {
    onLoad();
  }, []);

  useEffect(() => {
    if (hasNext && isVisible) {
      applyFilters({
        page,
        term: search,
        type: activeFilter,
      });
    }
  }, [isVisible]);

  useEffect(() => {
    if (brandSelected) {
      loadBalance();
      handleChangeFilter(activeFilter);
    }
  }, [brandSelected]);

  const onLoad = async () => {
    setLoadingPage(true);

    await loadBrands();

    setLoadingPage(false);
  };

  const getBalance = async () => {
    const internalBenefitAPI = new InternalBenefitAPI();
    const balance = await internalBenefitAPI.getBalanceByCustomerId({
      customerId: customer.id,
      brandId: brandSelected?.id,
    });
    return balance;
  };

  const loadBalance = async () => {
    const balance = await getBalance();
    setBalance(balance);
  };

  const loadBrands = async () => {
    const brandsResponse = await OlgaEnvironmentsAPI.getBrands();
    const brandsData = brandsResponse.getData([]);

    const [firstBrand] = brandsData;

    setBrandSelected(firstBrand);
    setBrands(brandsData);
  };

  const loadHistory = async ({ term, type, page, refresh = false }) => {
    setLoading(true);

    const limitByPage = 20;

    const internalBenefitAPI = new InternalBenefitAPI();

    const historyResponse = await internalBenefitAPI.getCashbackHistory({
      customerId: customer.id,
      brandId: brandSelected?.id,
      term,
      type,
      page,
      limit: limitByPage,
    });

    const eventPage = historyResponse.getData({
      total: 0,
      items: [],
      next: { page: -1, limit: limitByPage },
    });

    let newEvents = [];

    if (refresh) {
      newEvents = [...eventPage.items];
    } else {
      newEvents = [...events, ...eventPage.items];
    }

    setEvents(newEvents);
    setLength(eventPage.total);
    setHasNext(eventPage.next.page != -1);
    setPage(eventPage.next.page != -1 ? page + 1 : page);
    setLoading(false);
  };

  const applyFilters = async ({ term, type, page, refresh }) => {
    await loadHistory({
      page,
      term,
      type: type.id === "ALL" ? null : type.id,
      refresh,
    });
  };

  const debouncedSearch = useDebouncedCallback((nextValue) => {
    applyFilters({
      page: 0,
      term: nextValue,
      type: activeFilter,
      refresh: true,
    });
  }, 1000);

  const handleChangeFilter = (filter) => {
    setActiveFilter(filter);
    applyFilters({
      page: 0,
      term: search,
      type: filter,
      refresh: true,
    });
  };

  const cashbackProgram = localStorage.getItem("cashbackProgram");
  const program = JSON.parse(cashbackProgram);

  const programByPoint = program?.type === "POINT";

  return (
    <>
      {loadingPage && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}

      {!loadingPage && (
        <>
          <div className={styles.fidelityContainer}>
            <div className={styles.balance}>
              <div className="d-flex flex-column">
                <p className={styles.label}>
                  SALDO DE {programByPoint ? "PONTOS" : "CASHBACK"}
                </p>
                <p className={styles.total}>
                  {programByPoint ? balance : Currency.formatCurrency(balance)}
                </p>
              </div>
              <Button
                design="default-icon-mini-3"
                onClick={() => setOpenEditCashback(true)}
              >
                <i className="fal fa-pen" />
              </Button>
            </div>

            <div>
              <Select
                design="two"
                label="Marca"
                onChange={(e) => {
                  const brand = brands.find(
                    (b) => b.id === Number(e.target.value)
                  );
                  setBrandSelected(brand);
                }}
                className="mt-10"
                value={brandSelected?.id}
                options={brands.map((brand) => {
                  return {
                    label: brand.name,
                    value: brand.id,
                    selected: brand.id === brandSelected?.id,
                  };
                })}
              />
            </div>

            <div className={styles.extract}>
              <div className="d-flex align-center justify-between">
                <p className={styles.title}>Extrato ({length})</p>
                <div className={styles.filters}>
                  <div
                    className={css({
                      selectedFilter: true,
                      collapsed: collapsed,
                    })}
                    onClick={() => setCollapsed(!collapsed)}
                  >
                    <p>{activeFilter?.name}</p>
                    <i className="fas fa-chevron-down" />
                  </div>

                  <div
                    className={css({
                      collapsedCategories: true,
                      in: collapsed,
                      out: !collapsed,
                    })}
                  >
                    <ul className={styles.options}>
                      {(filters || [])
                        .filter((m) => m.id !== activeFilter?.id)
                        .map((m) => (
                          <li
                            key={m.name}
                            onClick={() => {
                              handleChangeFilter(m);
                              setCollapsed(false);
                            }}
                          >
                            {m.name}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>

              <InputSearch
                design="two"
                placeholder="Buscar por..."
                className="mt-15 mb-15"
                occurrences={events.length}
                onChange={(e) => {
                  setSearch(e.target.value);
                  debouncedSearch(e.target.value);
                }}
              />

              <div className={styles.events}>
                <If cond={events.length > 0}>
                  {events.map((event, index) => (
                    <CashbackEvent
                      key={index}
                      event={event}
                      programByPoint={programByPoint}
                    />
                  ))}
                </If>

                <If cond={events.length == 0 && !hasNext && !loading}>
                  <Empty>
                    <p>Nenhum evento encontrado</p>
                  </Empty>
                </If>

                <If cond={loading}>
                  <div className={styles.loading}>
                    <div className="w-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
                      <Loading />
                    </div>
                  </div>
                </If>

                <div ref={loadingRef}>&nbsp;</div>
              </div>
            </div>
          </div>

          <EditCashback
            customer={customer}
            brand={brandSelected}
            visible={openEditCashback}
            onClose={() => {
              setOpenEditCashback(false);
              loadBalance();
              applyFilters({
                page: 0,
                term: search,
                type: activeFilter,
                refresh: true,
              });
            }}
            programByPoint={!programByPoint}
          />
        </>
      )}
    </>
  );
};
